import { useEffect, useState } from 'react';
import axios from 'axios';
import { formatNumber } from '../../shared/numberFormat';

// Packages as /api/fulfilment returns them (issues #299, #313,
// packageService.describe): outermost first, each with the packages inside
// it (`packages`, at any depth). Every package has its own goods (`goods`)
// and its `contents` — its goods plus everything in the packages inside it
// ({ line_id, sku, name, uom, qty }). `name` is its own ("Box 2"), `label`
// says where it sits ("Box 2 in Satchel 1").

// Every package, the ones inside others included.
export function allPackages(packages) {
  return (packages || []).flatMap((p) => [p, ...allPackages(p.packages)]);
}

// The open package — at most one, never inside another.
export function openPackageOf(packages) {
  return (packages || []).find((p) => p.open) || null;
}

// Holds goods or other packages.
export function holdsAnything(pkg) {
  return !!pkg && ((pkg.goods || []).length > 0 || (pkg.packages || []).length > 0);
}

// The outermost packages Ship can take right now — mirrors
// packageService.shippableFrom: any with goods in it at some depth (the
// open one included, Ship closes it).
export function shippablePackages(packages) {
  return (packages || []).filter((p) => (p.contents || []).length > 0);
}

export function formatQty(n) {
  return formatNumber(Number(n || 0), { maxDecimals: 2 });
}

// "ABC-100 10 PCS, XYZ-200 2 PK".
export function contentsSummary(contents) {
  return (contents || []).map((c) => `${c.sku || '?'} ${formatQty(c.qty)}${c.uom ? ` ${c.uom}` : ''}`).join(', ');
}

// "2 packages inside" — or '' with none.
export function insideSummary(pkg) {
  const count = allPackages(pkg?.packages).length;
  return count ? `${count} package${count === 1 ? '' : 's'} inside` : '';
}

// A package's details as an editable draft, every value a string.
export function detailsDraft(pkg) {
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    weight: str(pkg?.weight),
    length: str(pkg?.length),
    width: str(pkg?.width),
    height: str(pkg?.height),
    packaging_id: pkg?.packaging_id ? String(pkg.packaging_id) : '',
  };
}

// What the API takes for a package's details — a blank saves as empty.
export function detailsPayload(draft) {
  const val = (v) => (v === '' || v === undefined ? null : v);
  return {
    weight: val(draft.weight),
    length: val(draft.length),
    width: val(draft.width),
    height: val(draft.height),
    packaging_id: draft.packaging_id ? Number(draft.packaging_id) : null,
  };
}

// Australia Post, recognised by name the way the backend does
// (services/carrierTracking.js) — issue #316.
export const AUSPOST_NAME = /australia\s*post|auspost/i;
export const isAustraliaPost = (name) => AUSPOST_NAME.test(String(name || ''));

// The Australia Post options a Shipping Option can be priced as (issue
// #316) — kept in step with australiaPostService.OPTIONS.
export const AUSPOST_OPTIONS = [
  { code: 'AUS_SERVICE_OPTION_SIGNATURE_ON_DELIVERY', name: 'Signature on Delivery' },
  { code: 'AUS_SERVICE_OPTION_EXTRA_COVER', name: 'Extra Cover' },
];

export const WEIGHT_TO_KG = { kg: 1, lb: 0.45359237 };

// An outermost shipped package's actual postage, ex GST (issue #350) —
// null until it's entered.
export function actualPostageOf(pkg) {
  return !pkg || pkg.actual_postage === null || pkg.actual_postage === undefined ? null : Number(pkg.actual_postage);
}

// The default Tax Code's rate (0.1 for 10%), null until loaded or when
// there's none — actual postage is typed in and shown inc GST at it.
export function useDefaultTaxRate() {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    axios.get('/api/tax-codes/default')
      .then((res) => setRate(res.data.data?.rate ?? null))
      .catch(() => setRate(null));
  }, []);
  return rate;
}

// An amount kept ex GST, inc GST to the cent, as a string for an input —
// blank when there's none.
export function incGstString(exAmount, taxRate) {
  if (exAmount === null || exAmount === undefined || exAmount === '') return '';
  return String(Math.round(Number(exAmount) * (1 + Number(taxRate || 0)) * 100) / 100);
}

// An Australia Post service's name from its code, for anyone who can't
// load Australia Post's own list.
export function auspostServiceName(code) {
  if (code === 'AUS_PARCEL_REGULAR') return 'Parcel Post';
  if (code === 'AUS_PARCEL_EXPRESS') return 'Express Post';
  return code || '';
}

// What a shipment charged for shipping, ex GST: its Invoice's shipping line
// once invoiced (issue #269; $0 on a later shipment's), else its order's
// whole shipping charge. null when there's none.
export function shipmentShippingCharge(shipment) {
  if (shipment?.invoice && !shipment.invoice.voided_at) return shipment.invoice.shipping_charge;
  return shipment?.order?.shipping_charge ?? null;
}
